import React, { useState, useEffect } from 'react';
import { useOpportunities } from '../../hooks/useOpportunities';
import { useUsers } from '../../hooks/useUsers';
import { useTasks } from '../../hooks/useTasks';
import { uploadOpportunityAttachment, deleteOpportunityAttachment } from '../../services/opportunitiesService';
import { useAuth } from '../../context/AuthContext';
import { Trash2, X, Globe, Save, AlertCircle, Paperclip, FileText, Loader2, Download, CheckSquare, Square, Plus, Clock } from 'lucide-react';
import { exportOpportunityToPdf } from '../../utils/opportunityPdf';
import ArchiveOpportunityModal from './ArchiveOpportunityModal';
import TaskHistoryModal from './TaskHistoryModal';

export default function OpportunityModal({ opportunity, onClose }) {
  const { 
    addOpportunity, 
    updateOpportunity, 
    deleteOpportunity, 
    addNoteToOpportunity, 
    deleteNoteFromOpportunity
  } = useOpportunities();
  
  const { users } = useUsers();
  const { currentUser } = useAuth();

  const bdUsers = (users || [])
    .filter(u => !u.deleted && u.status !== 'Disabled' && u.roles && (u.roles.includes('Business Development') || u.role === 'Business Development'))
    .map(u => ({
      id: u.id,
      name: u.name || (u.email ? u.email.split('@')[0] : 'Unnamed User'),
      email: u.email || ''
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
  
  const currentUserName = React.useMemo(() => {
    if (!currentUser) return '';
    const userDoc = (users || []).find(u => 
      u.email?.toLowerCase() === currentUser.email?.toLowerCase() || 
      u.id === currentUser.uid
    );
    if (userDoc?.name) return userDoc.name;
    if (currentUser.displayName) return currentUser.displayName;
    if (currentUser.email) {
      const prefix = currentUser.email.split('@')[0];
      return prefix.charAt(0).toUpperCase() + prefix.slice(1);
    }
    return '';
  }, [currentUser, users]);

  const { tasks, addTask: handleCreateTask, toggleTaskComplete: handleToggleTask, deleteTask: handleDeleteTask } = useTasks();
  
  // Tasks for this opportunity (active vs completed)
  const oppTasks = tasks.filter(t => t.oppId === opportunity?.id && !t.completed);
  const oppCompletedTasks = tasks.filter(t => t.oppId === opportunity?.id && t.completed);

  // Readonly for archived opportunities
  const isReadOnly = opportunity?.status === 'Archived';

  const isNew = opportunity?.isNew;

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    justification: '',
    dateAdded: new Date().toISOString().split('T')[0],
    status: 'New',
    jurisdiction: 'Federal',
    assignedTo: opportunity?.assignedTo || '',
    preSolicitationListDate: '',
    solicitationDate: '',
    dueDate: '',
    awardDate: '',
    contractDuration: '',
    estimatedValue: '',
    solicitationNumber: '',
    naicsCode: '',
    naicsDesc: '',
    locations: [],
    agencies: [],
    incumbent: '',
    incumbentUrl: '',
    currentStage: ''
  });

  const [urls, setUrls] = useState(['', '', '']);
  const [newNote, setNewNote] = useState('');
  const [liveNotes, setLiveNotes] = useState(opportunity?.notes || []);
  const [tempNotes, setTempNotes] = useState([]);
  const [tempAttachments, setTempAttachments] = useState([]);
  const [tempTasks, setTempTasks] = useState([]);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskDueDate, setNewTaskDueDate] = useState('');
  const [newTaskAssignedTo, setNewTaskAssignedTo] = useState('');
  const [isAddingTask, setIsAddingTask] = useState(false);
  const [isSavingTask, setIsSavingTask] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showTaskHistoryModal, setShowTaskHistoryModal] = useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [uploadingStates, setUploadingStates] = useState([]); // Array of { id, name, status: 'uploading' | 'success' | 'error' }

  const activeTempTasks = tempTasks.filter(t => !t.completed);
  const completedTempTasks = tempTasks.filter(t => t.completed);

  useEffect(() => {
    setIsConfirmingDelete(false);
    if (!isNew && opportunity) {
      setLiveNotes(opportunity.notes || []);
      setFormData({
        name: opportunity.name || '',
        shortDesc: opportunity.shortDesc || '',
        description: opportunity.description || '',
        justification: opportunity.justification || '',
        contractVehicle: opportunity.contractVehicle || '',
        dateAdded: opportunity.dateAdded || new Date().toISOString().split('T')[0],
        status: opportunity.status || 'New',
        jurisdiction: opportunity.jurisdiction || 'Federal',
        assignedTo: opportunity.assignedTo || opportunity.owner || opportunity.createdBy || opportunity.assignedUser || '',
        preSolicitationListDate: opportunity.preSolicitationListDate || '',
        solicitationDate: opportunity.solicitationDate || '',
        dueDate: opportunity.dueDate || '',
        awardDate: opportunity.awardDate || '',
        contractDuration: opportunity.contractDuration || '',
        estimatedValue: opportunity.estimatedValue !== undefined ? opportunity.estimatedValue : '',
        solicitationNumber: opportunity.solicitationNumber || '',
        naicsCode: opportunity.naicsCode || '',
        naicsDesc: opportunity.naicsDesc || '',
        locations: opportunity.locations || [],
        agencies: opportunity.agencies || [],
        pocs: opportunity.pocs || [],
        incumbent: opportunity.incumbent || '',
        incumbentUrl: opportunity.incumbentUrl || '',
        currentStage: opportunity.currentStage || ''
      });
      setUrls([
        opportunity.urls?.[0] || '',
        opportunity.urls?.[1] || '',
        opportunity.urls?.[2] || ''
      ]);
    } else {
      setFormData({
        name: '',
        shortDesc: '',
        description: '',
        justification: '',
        contractVehicle: '',
        dateAdded: new Date().toISOString().split('T')[0],
        status: opportunity?.status || 'New',
        jurisdiction: 'Federal',
        assignedTo: opportunity?.assignedTo || currentUserName || '',
        preSolicitationListDate: '',
        solicitationDate: '',
        dueDate: '',
        awardDate: '',
        contractDuration: '',
        estimatedValue: '',
        solicitationNumber: '',
        naicsCode: '',
        naicsDesc: '',
        locations: [],
        agencies: [],
        pocs: [],
        incumbent: '',
        incumbentUrl: '',
        currentStage: ''
      });
      setUrls(['', '', '']);
      setLiveNotes([]);
      setTempNotes([]);
      setTempAttachments([]);
      setTempTasks([]);
    }
  }, [isNew, opportunity, currentUserName]);

  // Simulated scanner for attachments stuck on "Scanning..."
  useEffect(() => {
    if (!isNew && opportunity && opportunity.attachments && opportunity.attachments.length > 0) {
      const unscanned = opportunity.attachments.filter(att => att.virusFree === undefined);
      if (unscanned.length > 0) {
        const timer = setTimeout(async () => {
          try {
            const updated = opportunity.attachments.map(att => {
              if (att.virusFree === undefined) {
                return { ...att, virusFree: true };
              }
              return att;
            });
            await updateOpportunity(opportunity.id, { attachments: updated });
          } catch (err) {
            console.error("Failed to update scanned attachments:", err);
          }
        }, 2000);
        return () => clearTimeout(timer);
      }
    }
  }, [opportunity?.attachments, isNew]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAddAgency = () => {
    setFormData(prev => ({
      ...prev,
      agencies: [...(prev.agencies || []), '']
    }));
  };

  const handleRemoveAgency = (index) => {
    setFormData(prev => ({
      ...prev,
      agencies: (prev.agencies || []).filter((_, i) => i !== index)
    }));
  };

  const handleAgencyChange = (index, val) => {
    const updated = [...(formData.agencies || [])];
    updated[index] = val;
    setFormData(prev => ({
      ...prev,
      agencies: updated
    }));
  };

  const handleAddLocation = () => {
    setFormData(prev => ({
      ...prev,
      locations: [...(prev.locations || []), '']
    }));
  };

  const handleRemoveLocation = (index) => {
    setFormData(prev => ({
      ...prev,
      locations: (prev.locations || []).filter((_, i) => i !== index)
    }));
  };

  const handleLocationChange = (index, val) => {
    const updated = [...(formData.locations || [])];
    updated[index] = val;
    setFormData(prev => ({
      ...prev,
      locations: updated
    }));
  };

  const handleAddPoc = () => {
    setFormData(prev => ({
      ...prev,
      pocs: [...(prev.pocs || []), { name: '', title: '', phone: '', email: '' }]
    }));
  };

  const handleRemovePoc = (index) => {
    setFormData(prev => ({
      ...prev,
      pocs: (prev.pocs || []).filter((_, i) => i !== index)
    }));
  };

  const handlePocChange = (index, field, val) => {
    const updated = [...(formData.pocs || [])];
    updated[index] = { ...updated[index], [field]: val };
    setFormData(prev => ({
      ...prev,
      pocs: updated
    }));
  };

  const formatCurrencyInput = (value) => {
    if (value === undefined || value === null || value === '') return '';
    const num = value.toString().replace(/[^0-9.]/g, '');
    if (isNaN(num) || num === '') return '';
    const parts = num.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  };

  const handleCurrencyChange = (e) => {
    const rawVal = e.target.value.replace(/[^0-9.]/g, '');
    setFormData(prev => ({
      ...prev,
      estimatedValue: rawVal ? parseFloat(rawVal) : ''
    }));
  };

  const handleExportPDF = () => {
    exportOpportunityToPdf(formData);
  };

  const handleRestoreOpportunity = async () => {
    try {
      setIsSaving(true);
      await updateOpportunity(opportunity.id, {
        status: opportunity.previousStatus || 'New',
        previousStatus: null,
        archived: false,
        archiveReason: null
      });
      onClose();
    } catch (err) {
      console.error("Failed to restore opportunity", err);
      alert("Failed to restore opportunity.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmArchive = async (reason) => {
    try {
      setIsSaving(true);
      await updateOpportunity(opportunity.id, {
        status: 'Archived',
        previousStatus: opportunity.status,
        archived: true,
        archiveReason: reason,
        archivedAt: new Date().toISOString()
      });
      setShowArchiveModal(false);
      onClose();
    } catch (err) {
      console.error("Failed to archive opportunity", err);
      alert("Failed to archive opportunity.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleUrlChange = (index, val) => {
    const updated = [...urls];
    updated[index] = val;
    setUrls(updated);
  };

  const formatExternalLink = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    return `https://${url}`;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      if (isNew) {
        const oppId = await addOpportunity({
          ...formData,
          urls
        });

        if (tempAttachments.length > 0) {
          const uploaded = [];
          for (const temp of tempAttachments) {
            const att = await uploadOpportunityAttachment(oppId, temp.file);
            uploaded.push(att);
          }
          await updateOpportunity(oppId, { attachments: uploaded });
        }
        
        for (const note of tempNotes) {
          await addNoteToOpportunity(oppId, note.text, note.author);
        }

        for (const task of tempTasks) {
          await handleCreateTask({
            ...task,
            oppId: oppId,
            oppName: formData.shortDesc || formData.name
          });
        }
      } else {
        await updateOpportunity(opportunity.id, {
          ...formData,
          urls
        });
      }
      onClose();
    } catch (err) {
      console.error("Failed to save opportunity", err);
      alert("Failed to save opportunity.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddOpportunityTask = async () => {
    if (!newTaskTitle.trim()) return;
    setIsSavingTask(true);
    const taskPayload = {
      title: newTaskTitle.trim(),
      dueDate: newTaskDueDate || '',
      assignedTo: newTaskAssignedTo || formData.assignedTo || currentUserName || '',
      completed: false,
      type: 'custom',
      oppId: opportunity?.id || '',
      oppName: formData.shortDesc || formData.name || opportunity?.name || ''
    };

    if (isNew) {
      setTempTasks(prev => [...prev, { ...taskPayload, id: 'temp-' + Date.now() }]);
      setNewTaskTitle('');
      setNewTaskDueDate('');
      setNewTaskAssignedTo('');
      setIsAddingTask(false);
      setIsSavingTask(false);
    } else {
      try {
        await handleCreateTask(taskPayload);
        setNewTaskTitle('');
        setNewTaskDueDate('');
        setNewTaskAssignedTo('');
        setIsAddingTask(false);
      } catch (err) {
        console.error("Failed to add task", err);
        alert("Failed to add task: " + (err.message || "Unknown error"));
      } finally {
        setIsSavingTask(false);
      }
    }
  };

  const handleRemoveOpportunityTask = async (taskId) => {
    if (isNew) {
      setTempTasks(prev => prev.filter(t => t.id !== taskId));
    } else {
      try {
        await handleDeleteTask(taskId);
      } catch (err) {
        console.error("Failed to delete task", err);
      }
    }
  };

  const handleToggleOpportunityTask = async (task) => {
    const completerName = currentUserName || currentUser?.displayName || (currentUser?.email ? currentUser.email.split('@')[0] : '') || 'Unknown User';
    if (isNew) {
      setTempTasks(prev => prev.map(t => {
        if (t.id === task.id) {
          const nextCompleted = !t.completed;
          return {
            ...t,
            completed: nextCompleted,
            completedAt: nextCompleted ? new Date().toISOString() : null,
            completedBy: nextCompleted ? completerName : null
          };
        }
        return t;
      }));
    } else {
      try {
        await handleToggleTask(task.id, task.completed, completerName);
      } catch (err) {
        console.error("Failed to toggle task", err);
      }
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    const author = currentUserName || currentUser?.displayName || (currentUser?.email ? currentUser.email.split('@')[0] : '') || 'Unknown';
    const noteText = newNote.trim();
    setNewNote('');

    if (isNew) {
      setTempNotes(prev => [...prev, {
        id: 'temp-' + Date.now(),
        text: noteText,
        createdAt: new Date().toISOString(),
        author
      }]);
    } else {
      const newNoteObj = {
        id: 'note-' + Date.now(),
        text: noteText,
        author,
        timestamp: new Date().toISOString()
      };
      setLiveNotes(prev => [...prev, newNoteObj]);
      try {
        await addNoteToOpportunity(opportunity.id, noteText, author);
      } catch (err) {
        console.error("Failed to add note", err);
        setLiveNotes(prev => prev.filter(n => n.id !== newNoteObj.id));
      }
    }
  };

  const handleRemoveNote = async (note) => {
    if (isNew) {
      setTempNotes(prev => prev.filter(n => n.id !== note.id));
    } else {
      if (window.confirm("Remove this note?")) {
        setLiveNotes(prev => prev.filter(n => n.id !== note.id && (note.timestamp ? n.timestamp !== note.timestamp : true)));
        try {
          await deleteNoteFromOpportunity(opportunity.id, note);
        } catch (err) {
          console.error("Failed to remove note", err);
          setLiveNotes(prev => [...prev, note]);
        }
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDraggingOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDraggingOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDraggingOver(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length > 0) {
      processFiles(files);
    }
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      processFiles(files);
    }
  };

  const processFiles = async (files) => {
    if (isNew) {
      const newTemps = files.map(file => ({
        id: 'temp-' + Date.now() + '-' + Math.random(),
        file,
        name: file.name
      }));
      setTempAttachments(prev => [...prev, ...newTemps]);
      
      const newUploads = files.map(file => ({
        id: 'upload-' + Date.now() + '-' + Math.random(),
        name: file.name,
        status: 'success'
      }));
      setUploadingStates(prev => [...prev, ...newUploads]);
      setTimeout(() => {
        setUploadingStates(prev => prev.filter(u => !newUploads.some(n => n.id === u.id)));
      }, 4000);
    } else {
      setIsUploading(true);
      const newUploads = files.map(file => ({
        id: 'upload-' + Date.now() + '-' + Math.random(),
        name: file.name,
        status: 'uploading'
      }));
      setUploadingStates(prev => [...prev, ...newUploads]);

      try {
        const uploadPromises = files.map(async (file, idx) => {
          const tempId = newUploads[idx].id;
          try {
            const att = await uploadOpportunityAttachment(opportunity.id, file);
            setUploadingStates(prev => prev.map(u => u.id === tempId ? { ...u, status: 'success' } : u));
            return att;
          } catch (err) {
            console.error("Individual file upload failed", err);
            setUploadingStates(prev => prev.map(u => u.id === tempId ? { ...u, status: 'error' } : u));
            return null;
          }
        });

        const results = await Promise.all(uploadPromises);
        const successfulUploads = results.filter(Boolean);

        if (successfulUploads.length > 0) {
          const updated = [...(opportunity.attachments || []), ...successfulUploads];
          await updateOpportunity(opportunity.id, { attachments: updated });
        }

        setTimeout(() => {
          setUploadingStates(prev => prev.filter(u => !newUploads.some(n => n.id === u.id)));
        }, 4000);
      } catch (err) {
        console.error("File upload failed", err);
        alert("Upload failed.");
      } finally {
        setIsUploading(false);
      }
    }
  };

  const handleRemoveAttachment = async (att) => {
    if (isNew) {
      setTempAttachments(prev => prev.filter(a => a.id !== att.id));
    } else {
      if (window.confirm("Delete this attachment forever?")) {
        try {
          if (att.path) await deleteOpportunityAttachment(att.path);
          const updated = (opportunity.attachments || []).filter(a => a.id !== att.id);
          await updateOpportunity(opportunity.id, { attachments: updated });
        } catch (err) {
          console.error("Failed to remove attachment", err);
          alert("Failed to delete attachment.");
        }
      }
    }
  };

  const handleDeleteOpportunity = async () => {
    if (isNew) return;
    try {
      if (opportunity.attachments && opportunity.attachments.length > 0) {
        for (const att of opportunity.attachments) {
          if (att.path) {
            try { await deleteOpportunityAttachment(att.path); } catch(e) {}
          }
        }
      }
      await deleteOpportunity(opportunity.id);
      onClose();
    } catch (err) {
      console.error("Failed to delete opportunity", err);
      alert("Failed to delete opportunity.");
    }
  };

  if (!opportunity) return null;

  return (
    <div style={{
      position: 'fixed', top: 0, right: 0, bottom: 0, width: '850px', maxWidth: '90%',
      backgroundColor: 'var(--bg-card)', boxShadow: '-5px 0 15px rgba(0,0,0,0.5)',
      zIndex: 1000, display: 'flex', flexDirection: 'column'
    }}>
      <div style={{ padding: '1.5rem', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0, color: 'var(--primary)' }}>
          {isNew ? 'New Opportunity' : 'Edit Opportunity'}
        </h3>
        <button type="button" onClick={() => onClose()} style={{ background: 'none', border: 'none', fontSize: '1.5rem', color: 'var(--text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
          <X size={20} />
        </button>
      </div>

      <div style={{ padding: '1.5rem', overflowY: 'auto', flex: 1 }}>
        <form id="opportunityForm" onSubmit={handleSave}>
          
           {/* General Fields Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 0.9fr 1fr 1fr', gap: '1rem', marginBottom: '1.25rem' }}>
            <div className="form-group">
              <label className="form-label">Name of Opportunity *</label>
              <input 
                disabled={isReadOnly} 
                required 
                name="name" 
                className="form-input" 
                value={formData.name} 
                onChange={handleChange} 
                placeholder="Enter opportunity name..."
              />
            </div>
            <div className="form-group">
              <label className="form-label">Short Desc *</label>
              <input 
                disabled={isReadOnly} 
                required 
                name="shortDesc" 
                className="form-input" 
                value={formData.shortDesc || ''} 
                onChange={handleChange} 
                placeholder="For Kanban board display..."
              />
            </div>
            <div className="form-group">
              <label className="form-label">Government Level</label>
              <select 
                disabled={isReadOnly}
                name="jurisdiction" 
                className="form-input" 
                value={formData.jurisdiction || 'Federal'} 
                onChange={handleChange}
              >
                <option value="Federal">Federal</option>
                <option value="State">State</option>
                <option value="Local">Local</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Solicitation Number</label>
              <input 
                disabled={isReadOnly} 
                name="solicitationNumber" 
                className="form-input" 
                value={formData.solicitationNumber || ''} 
                onChange={handleChange} 
                placeholder="e.g. SOL-12345"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Current Stage</label>
              <select 
                disabled={isReadOnly}
                name="currentStage" 
                className="form-input" 
                value={formData.currentStage || ''} 
                onChange={handleChange}
              >
                <option value="">-- Select Stage --</option>
                <option value="Sources Sought">Sources Sought</option>
                <option value="Pre-RFP">Pre-RFP</option>
                <option value="Post-RFP">Post-RFP</option>
                <option value="Expired">Expired</option>
                <option value="Open Enrollment">Open Enrollment</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.2fr 1fr 1fr', gap: '1rem', marginBottom: '1.25rem' }}>
            <div className="form-group">
              <label className="form-label">Date Added (Auto)</label>
              <input 
                disabled 
                className="form-input" 
                style={{ opacity: 0.7 }}
                value={formData.dateAdded} 
              />
            </div>
            <div className="form-group">
              <label className="form-label">Stage / Column</label>
              <select 
                disabled={isReadOnly}
                name="status" 
                className="form-input" 
                value={formData.status} 
                onChange={handleChange}
              >
                <option value="New">New</option>
                <option value="Presented">Presented</option>
                <option value="Approved">Approved</option>
                <option value="Proposal">Proposal</option>
                <option value="Submitted">Submitted</option>
                <option value="Won">Won</option>
                <option value="Lost">Lost</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Assigned BD Lead</label>
              <select 
                disabled={isReadOnly}
                name="assignedTo" 
                className="form-input" 
                value={formData.assignedTo || ''} 
                onChange={handleChange}
              >
                <option value="">-- Unassigned --</option>
                {bdUsers.map(u => (
                  <option key={u.id} value={u.name}>
                    {u.name}
                  </option>
                ))}
                {currentUserName && !bdUsers.some(u => u.name === currentUserName) && (
                  <option value={currentUserName}>
                    {currentUserName}
                  </option>
                )}
                {formData.assignedTo && formData.assignedTo !== currentUserName && !bdUsers.some(u => u.name === formData.assignedTo) && (
                  <option value={formData.assignedTo}>
                    {formData.assignedTo}
                  </option>
                )}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Estimated Value ($)</label>
              <input 
                disabled={isReadOnly} 
                type="text" 
                className="form-input" 
                value={formatCurrencyInput(formData.estimatedValue)} 
                onChange={handleCurrencyChange}
                placeholder="e.g. 1,250,000.00"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Duration of Contract</label>
              <input 
                disabled={isReadOnly} 
                name="contractDuration" 
                className="form-input" 
                value={formData.contractDuration || ''} 
                onChange={handleChange} 
                placeholder="e.g. 5 Years"
              />
            </div>
          </div>

          {/* NAICS Section Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '1rem', marginBottom: '1.25rem' }}>
            <div className="form-group">
              <label className="form-label">NAICS Code</label>
              <input 
                disabled={isReadOnly} 
                type="number"
                name="naicsCode" 
                className="form-input" 
                value={formData.naicsCode || ''} 
                onChange={handleChange} 
                placeholder="e.g. 541511"
              />
            </div>
            <div className="form-group">
              <label className="form-label">NAICS Description</label>
              <input 
                disabled={isReadOnly} 
                name="naicsDesc" 
                className="form-input" 
                value={formData.naicsDesc || ''} 
                onChange={handleChange} 
                placeholder="e.g. Custom Computer Programming Services"
              />
            </div>
          </div>

          {/* Incumbent Section Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.25rem' }}>
            <div className="form-group">
              <label className="form-label">Incumbent</label>
              <input 
                disabled={isReadOnly} 
                name="incumbent" 
                className="form-input" 
                value={formData.incumbent || ''} 
                onChange={handleChange} 
                placeholder="e.g. Lockheed Martin"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Incumbent URL</label>
              <input 
                disabled={isReadOnly} 
                name="incumbentUrl" 
                className="form-input" 
                value={formData.incumbentUrl || ''} 
                onChange={handleChange} 
                placeholder="e.g. www.lockheedmartin.com"
              />
            </div>
          </div>

          {/* Key Dates Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1rem', marginBottom: '1.25rem' }}>
            <div className="form-group">
              <label className="form-label" style={{ fontSize: '0.8rem' }}>Pre-Sol List Date</label>
              <input 
                disabled={isReadOnly} 
                type="date"
                name="preSolicitationListDate" 
                className="form-input" 
                value={formData.preSolicitationListDate || ''} 
                onChange={handleChange} 
              />
            </div>
            <div className="form-group">
              <label className="form-label" style={{ fontSize: '0.8rem' }}>Solicitation Date</label>
              <input 
                disabled={isReadOnly} 
                type="date"
                name="solicitationDate" 
                className="form-input" 
                value={formData.solicitationDate || ''} 
                onChange={handleChange} 
              />
            </div>
            <div className="form-group">
              <label className="form-label" style={{ fontSize: '0.8rem' }}>Proposal Due Date</label>
              <input 
                disabled={isReadOnly} 
                type="date"
                name="dueDate" 
                className="form-input" 
                value={formData.dueDate || ''} 
                onChange={handleChange} 
              />
            </div>
            <div className="form-group">
              <label className="form-label" style={{ fontSize: '0.8rem' }}>Award Date</label>
              <input 
                disabled={isReadOnly} 
                type="date"
                name="awardDate" 
                className="form-input" 
                value={formData.awardDate || ''} 
                onChange={handleChange} 
              />
            </div>
          </div>

          {/* Dynamic Agency & Location Inputs */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.25rem' }}>
            
            {/* Agencies Card */}
            <div style={{ border: '1px solid var(--border-color)', borderRadius: '8px', padding: '1rem', backgroundColor: 'rgba(255,255,255,0.01)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <label className="form-label" style={{ margin: 0 }}>Agency / Agencies</label>
                <button type="button" className="btn btn-secondary" style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', minHeight: 'auto' }} onClick={handleAddAgency}>
                  + Add
                </button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '150px', overflowY: 'auto' }}>
                {(!formData.agencies || formData.agencies.length === 0) ? (
                  <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontStyle: 'italic' }}>No agencies specified.</span>
                ) : (
                  formData.agencies.map((agency, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <input 
                        type="text" 
                        className="form-input"
                        placeholder="e.g. Dept of Defense"
                        value={agency} 
                        onChange={(e) => handleAgencyChange(idx, e.target.value)}
                        disabled={isReadOnly}
                      />
                      <button type="button" onClick={() => handleRemoveAgency(idx)} style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer' }}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Location of Performance Card */}
            <div style={{ border: '1px solid var(--border-color)', borderRadius: '8px', padding: '1rem', backgroundColor: 'rgba(255,255,255,0.01)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <label className="form-label" style={{ margin: 0 }}>Location of Performance</label>
                <button type="button" className="btn btn-secondary" style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', minHeight: 'auto' }} onClick={handleAddLocation}>
                  + Add
                </button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '150px', overflowY: 'auto' }}>
                {(!formData.locations || formData.locations.length === 0) ? (
                  <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontStyle: 'italic' }}>No locations specified.</span>
                ) : (
                  formData.locations.map((locStr, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <input 
                        type="text" 
                        className="form-input"
                        placeholder="e.g. Washington, DC"
                        value={locStr} 
                        onChange={(e) => handleLocationChange(idx, e.target.value)}
                        disabled={isReadOnly}
                      />
                      <button type="button" onClick={() => handleRemoveLocation(idx)} style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer' }}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>

          </div>

          {/* Points of Contact (POCs) Card */}
          <div style={{ border: '1px solid var(--border-color)', borderRadius: '8px', padding: '1rem', marginBottom: '1.25rem', backgroundColor: 'rgba(255,255,255,0.01)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <label className="form-label" style={{ margin: 0 }}>Points of Contact (POCs)</label>
              <button type="button" className="btn btn-secondary" style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', minHeight: 'auto' }} onClick={handleAddPoc}>
                + Add POC
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxHeight: '300px', overflowY: 'auto' }}>
              {(!formData.pocs || formData.pocs.length === 0) ? (
                <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontStyle: 'italic' }}>No POCs specified.</span>
              ) : (
                formData.pocs.map((poc, idx) => (
                  <div key={idx} style={{ position: 'relative', border: '1px dashed var(--border-color)', borderRadius: '6px', padding: '0.75rem', backgroundColor: 'rgba(0,0,0,0.1)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--primary)' }}>POC #{idx + 1}</span>
                      <button type="button" onClick={() => handleRemovePoc(idx)} style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.75rem' }}>
                        <Trash2 size={12} /> Remove
                      </button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <div>
                        <label style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '0.25rem' }}>Name</label>
                        <input 
                          type="text" 
                          className="form-input" 
                          placeholder="e.g. John Doe"
                          value={poc.name || ''} 
                          onChange={(e) => handlePocChange(idx, 'name', e.target.value)}
                          disabled={isReadOnly}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '0.25rem' }}>Title</label>
                        <input 
                          type="text" 
                          className="form-input" 
                          placeholder="e.g. Contracting Officer"
                          value={poc.title || ''} 
                          onChange={(e) => handlePocChange(idx, 'title', e.target.value)}
                          disabled={isReadOnly}
                        />
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                      <div>
                        <label style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '0.25rem' }}>Phone</label>
                        <input 
                          type="text" 
                          className="form-input" 
                          placeholder="e.g. (123) 456-7890"
                          value={poc.phone || ''} 
                          onChange={(e) => handlePocChange(idx, 'phone', e.target.value)}
                          disabled={isReadOnly}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '0.25rem' }}>Email</label>
                        <input 
                          type="email" 
                          className="form-input" 
                          placeholder="e.g. john.doe@agency.gov"
                          value={poc.email || ''} 
                          onChange={(e) => handlePocChange(idx, 'email', e.target.value)}
                          disabled={isReadOnly}
                        />
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="form-group" style={{ marginBottom: '1.25rem' }}>
            <label className="form-label">Description</label>
            <textarea 
              disabled={isReadOnly} 
              name="description" 
              className="form-input" 
              style={{ minHeight: '80px', resize: 'vertical' }}
              value={formData.description} 
              onChange={handleChange}
              placeholder="Describe this opportunity..."
            />
          </div>

          <div className="form-group" style={{ marginBottom: '1.25rem' }}>
            <label className="form-label">Contract Vehicle</label>
            <textarea 
              disabled={isReadOnly} 
              name="contractVehicle" 
              className="form-input" 
              style={{ minHeight: '80px', resize: 'vertical' }}
              value={formData.contractVehicle || ''} 
              onChange={handleChange}
              placeholder="e.g. OASIS, GSA MAS, SEWP V, etc..."
            />
          </div>

          <div className="form-group" style={{ marginBottom: '1.5rem' }}>
            <label className="form-label">Justification (Paragraph)</label>
            <textarea 
              disabled={isReadOnly} 
              name="justification" 
              className="form-input" 
              style={{ minHeight: '120px', resize: 'vertical' }}
              value={formData.justification} 
              onChange={handleChange}
              placeholder="Provide context or justification on why this opportunity is being pursued..."
            />
          </div>

          <details open style={{ marginBottom: '1.5rem', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '1rem' }}>
            <summary style={{ cursor: 'pointer', color: 'var(--primary)', fontWeight: 600, fontSize: '0.875rem' }}>
              URL Links (Max 3)
            </summary>
            <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {[0, 1, 2].map((idx) => (
                <div key={idx} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', minWidth: '45px' }}>Link {idx + 1}:</span>
                  <input 
                    disabled={isReadOnly} 
                    type="text" 
                    className="form-input" 
                    placeholder="e.g. www.google.com" 
                    value={urls[idx]} 
                    onChange={(e) => handleUrlChange(idx, e.target.value)}
                  />
                  {urls[idx] && (
                    <a 
                      href={formatExternalLink(urls[idx])} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="btn btn-secondary"
                      style={{ padding: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      title="Open Link"
                    >
                      <Globe size={16} />
                    </a>
                  )}
                </div>
              ))}
            </div>
          </details>

          <style>{`
            @keyframes spin-mini {
              to { transform: rotate(360deg); }
            }
          `}</style>

          <div style={{ marginBottom: '1.5rem', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '1rem' }}>
            <label className="form-label" style={{ marginBottom: '0.75rem', display: 'block' }}>File Attachments</label>
            
            {!isReadOnly && (
              <div 
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => document.getElementById('file-input-trigger').click()}
                style={{
                  border: isDraggingOver ? '2px dashed #00e096' : '2px dashed var(--border-color)',
                  backgroundColor: isDraggingOver ? 'rgba(0, 224, 150, 0.05)' : 'rgba(255,255,255,0.01)',
                  boxShadow: isDraggingOver ? '0 0 10px rgba(0, 224, 150, 0.2)' : 'none',
                  borderRadius: '8px',
                  padding: '1.5rem',
                  textAlign: 'center',
                  cursor: 'pointer',
                  marginBottom: '1rem',
                  transition: 'all 0.2s ease-in-out',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                <input 
                  id="file-input-trigger"
                  type="file" 
                  multiple 
                  onChange={handleFileUpload} 
                  style={{ display: 'none' }} 
                />
                <Paperclip size={24} style={{ color: isDraggingOver ? '#00e096' : 'var(--text-secondary)' }} />
                <span style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-primary)' }}>
                  Drag & drop files here, or <span style={{ color: 'var(--primary)', textDecoration: 'underline' }}>browse</span>
                </span>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                  Supports multiple PDFs, docs, images
                </span>
              </div>
            )}

            {/* Individual uploads real-time feedback */}
            {uploadingStates.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', marginBottom: '1rem', padding: '0.5rem', backgroundColor: 'rgba(0,0,0,0.15)', borderRadius: '6px' }}>
                {uploadingStates.map(item => (
                  <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.78rem' }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '75%', color: 'var(--text-primary)' }}>
                      {item.name}
                    </span>
                    {item.status === 'uploading' && (
                      <span style={{ color: '#F59E0B', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                        <span style={{ width: '10px', height: '10px', border: '2px solid #F59E0B', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin-mini 0.8s linear infinite' }}></span>
                        Uploading...
                      </span>
                    )}
                    {item.status === 'success' && (
                      <span style={{ color: '#00e096', fontWeight: 500 }}>✓ Uploaded</span>
                    )}
                    {item.status === 'error' && (
                      <span style={{ color: '#EF4444', fontWeight: 500 }}>✗ Failed</span>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {!isNew && opportunity.attachments && opportunity.attachments.map(att => (
                <div key={att.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'rgba(255,255,255,0.02)', padding: '0.5rem 0.75rem', borderRadius: '4px', border: '1px solid var(--border-color)', gap: '1rem' }}>
                  {att.virusFree === true ? (
                    <a href={att.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.8125rem', fontWeight: 500, color: 'var(--primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '80%', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                      <FileText size={12} />
                      {att.name}
                    </a>
                  ) : att.virusFree === false ? (
                    <span style={{ fontSize: '0.8125rem', fontWeight: 500, color: 'var(--danger)', display: 'inline-flex', alignItems: 'center', gap: '0.25rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '80%' }}>
                      <FileText size={12} />
                      {att.name} (Virus Detected!)
                    </span>
                  ) : (
                    <span style={{ fontSize: '0.8125rem', fontWeight: 500, color: 'var(--text-secondary)', display: 'inline-flex', alignItems: 'center', gap: '0.25rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '80%' }}>
                      <Loader2 size={12} className="animate-spin" />
                      {att.name} (Scanning...)
                    </span>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    {att.virusFree === true && (
                      <a 
                        href={att.url} 
                        download={att.name} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ color: 'var(--primary)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        title="Download attachment"
                      >
                        <Download size={14} />
                      </a>
                    )}
                    {!isReadOnly && (
                      <button 
                        type="button" 
                        onClick={() => handleRemoveAttachment(att)} 
                        style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
                        title="Delete attachment"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
              ))}

              {isNew && tempAttachments.map(temp => (
                <div key={temp.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'rgba(255,255,255,0.02)', padding: '0.5rem 0.75rem', borderRadius: '4px', border: '1px solid var(--border-color)' }}>
                  <span style={{ fontSize: '0.8125rem', fontWeight: 500, color: 'var(--primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '80%' }}>
                    {temp.name} <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', fontStyle: 'italic' }}>(Staged)</span>
                  </span>
                  <button type="button" onClick={() => handleRemoveAttachment(temp)} style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer' }}>
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}

              {((!isNew && (!opportunity.attachments || opportunity.attachments.length === 0)) || (isNew && tempAttachments.length === 0)) && (
                <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', fontStyle: 'italic', padding: '0.25rem' }}>
                  No file attachments.
                </div>
              )}
            </div>
          </div>

        </form>

        {/* Tasks & Weekly Touches Section */}
        <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '1.5rem', marginTop: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h4 style={{ margin: 0, color: 'var(--primary)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <CheckSquare size={18} /> Tasks & Weekly Touches
            </h4>
            {!isReadOnly && !isAddingTask && (
              <button 
                type="button" 
                className="btn btn-secondary" 
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.6rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                onClick={() => {
                  setNewTaskAssignedTo(formData.assignedTo || currentUserName || '');
                  setIsAddingTask(true);
                }}
              >
                <Plus size={14} /> Add Task
              </button>
            )}
          </div>

          {/* New Task Inline Form */}
          {isAddingTask && !isReadOnly && (
            <div style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '1rem', marginBottom: '1rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: '0.75rem', alignItems: 'flex-end' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label" style={{ fontSize: '0.75rem' }}>Task Description *</label>
                  <input
                    className="form-input"
                    placeholder="e.g. Follow up with contracting officer..."
                    value={newTaskTitle}
                    onChange={e => setNewTaskTitle(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddOpportunityTask();
                      }
                    }}
                    autoFocus
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label" style={{ fontSize: '0.75rem' }}>Due Date</label>
                  <input
                    type="date"
                    className="form-input"
                    value={newTaskDueDate}
                    onChange={e => setNewTaskDueDate(e.target.value)}
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label" style={{ fontSize: '0.75rem' }}>Assignee (BD)</label>
                  <select
                    className="form-input"
                    value={newTaskAssignedTo}
                    onChange={e => setNewTaskAssignedTo(e.target.value)}
                  >
                    <option value="">-- Select Assignee --</option>
                    {bdUsers.map(u => (
                      <option key={u.id} value={u.name}>{u.name}</option>
                    ))}
                    {currentUserName && !bdUsers.some(u => u.name === currentUserName) && (
                      <option value={currentUserName}>{currentUserName}</option>
                    )}
                  </select>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button 
                    type="button" 
                    className="btn btn-primary" 
                    style={{ padding: '0.4rem 0.75rem', fontSize: '0.8125rem' }} 
                    onClick={handleAddOpportunityTask}
                    disabled={isSavingTask || !newTaskTitle.trim()}
                  >
                    {isSavingTask ? 'Saving...' : 'Save'}
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    style={{ padding: '0.4rem 0.75rem', fontSize: '0.8125rem' }} 
                    onClick={() => setIsAddingTask(false)}
                    disabled={isSavingTask}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Task List */}
          <div style={{ maxHeight: '220px', overflowY: 'auto', backgroundColor: 'rgba(255,255,255,0.02)', padding: '0.75rem', borderRadius: '8px', display: 'flex', flexDirection: 'column', gap: '0.5rem', border: '1px solid var(--border-color)' }}>
            {(() => {
              const displayTasks = isNew ? activeTempTasks : oppTasks;
              if (displayTasks.length === 0) {
                return <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: '0.5rem 0', fontStyle: 'italic' }}>No active tasks for this opportunity.</p>;
              }
              return displayTasks.map(task => (
                <div 
                  key={task.id} 
                  style={{ 
                    padding: '0.6rem 0.75rem', 
                    borderRadius: '6px',
                    backgroundColor: 'rgba(255,255,255,0.04)',
                    border: '1px solid var(--border-color)',
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    gap: '0.75rem'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1, minWidth: 0 }}>
                    <button
                      type="button"
                      disabled={isReadOnly}
                      onClick={() => handleToggleOpportunityTask(task)}
                      style={{ background: 'none', border: 'none', cursor: isReadOnly ? 'default' : 'pointer', color: 'var(--text-secondary)', padding: 0, display: 'flex', alignItems: 'center' }}
                      title="Clear / Complete Task"
                    >
                      <Square size={18} />
                    </button>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ 
                        fontSize: '0.875rem', 
                        color: 'var(--text-primary)', 
                        fontWeight: 500
                      }}>
                        {task.title}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', display: 'flex', gap: '0.75rem', marginTop: '0.2rem' }}>
                        {task.assignedTo && <span>👤 {task.assignedTo}</span>}
                        {task.dueDate && <span>📅 Due: {task.dueDate}</span>}
                        {task.type === 'weekly_touch' && <span style={{ color: '#3B82F6', fontWeight: 500 }}>🔄 Weekly Touch</span>}
                      </div>
                    </div>
                  </div>
                  {!isReadOnly && (
                    <button 
                      type="button" 
                      onClick={() => handleRemoveOpportunityTask(task.id)} 
                      style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', padding: '0.25rem' }}
                      title="Delete task"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ));
            })()}
          </div>
        </div>

        <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '1.5rem', marginTop: '1.5rem' }}>
          <h4 style={{ marginBottom: '1rem', color: 'var(--primary)' }}>Notes & Comments</h4>

          <div style={{ marginBottom: '1rem', maxHeight: '200px', overflowY: 'auto', backgroundColor: 'rgba(255,255,255,0.02)', padding: '1rem', borderRadius: '8px', display: 'flex', flexDirection: 'column', gap: '0.75rem', border: '1px solid var(--border-color)' }}>
            {isNew ? (
              tempNotes.length === 0 ? (
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: 0 }}>No notes added yet.</p>
              ) : (
                tempNotes.map(note => (
                  <div key={note.id} style={{ paddingBottom: '0.5rem', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
                        {new Date(note.createdAt).toLocaleString()} • {note.author} (Draft)
                      </div>
                      <div style={{ fontSize: '0.875rem', color: 'var(--text-primary)' }}>{note.text}</div>
                    </div>
                    <button type="button" onClick={() => handleRemoveNote(note)} style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', fontSize: '0.75rem' }}>
                      Remove
                    </button>
                  </div>
                ))
              )
            ) : (
              (!liveNotes || liveNotes.length === 0) ? (
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: 0 }}>No notes yet.</p>
              ) : (
                liveNotes.map(note => (
                  <div key={note.id || (note.timestamp + note.author)} style={{ paddingBottom: '0.5rem', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
                        {note.timestamp ? new Date(note.timestamp).toLocaleString() : ''} • {note.author}
                      </div>
                      <div style={{ fontSize: '0.875rem', color: 'var(--text-primary)' }}>{note.text}</div>
                    </div>
                    {!isReadOnly && (
                      <button type="button" onClick={() => handleRemoveNote(note)} style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', fontSize: '0.75rem' }}>
                        Remove
                      </button>
                    )}
                  </div>
                ))
              )
            )}
          </div>

          {!isReadOnly && (
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input 
                className="form-input" 
                placeholder="Add a timestamped note..." 
                value={newNote} 
                onChange={e => setNewNote(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddNote();
                  }
                }}
              />
              <button type="button" className="btn btn-secondary" onClick={handleAddNote}>Add</button>
            </div>
          )}
        </div>
      </div>

      <div style={{ padding: '1.5rem', borderTop: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', backgroundColor: 'var(--bg-main)' }}>
        {!isNew && !isReadOnly ? (
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            {!isConfirmingDelete ? (
              <button 
                type="button" 
                style={{ color: '#EF4444', background: 'none', border: 'none', display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                onClick={() => setIsConfirmingDelete(true)}
                title="Delete Opportunity"
              >
                <Trash2 size={20} />
              </button>
            ) : (
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <span style={{ color: '#EF4444', fontSize: '0.8125rem', fontWeight: 600 }}>Confirm Delete?</span>
                <button 
                  type="button" 
                  className="btn btn-primary" 
                  style={{ backgroundColor: '#EF4444', borderColor: '#EF4444', padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                  onClick={handleDeleteOpportunity}
                >
                  Yes
                </button>
                <button 
                  type="button" 
                  style={{ background: 'none', border: 'none', fontSize: '0.75rem', fontWeight: 500, color: 'var(--text-secondary)', cursor: 'pointer' }}
                  onClick={() => setIsConfirmingDelete(false)}
                >
                  No
                </button>
              </div>
            )}

            {opportunity.status === 'Archived' ? (
              <button
                type="button"
                className="btn btn-secondary"
                style={{ fontSize: '0.75rem', padding: '0.4rem 0.75rem', display: 'flex', alignItems: 'center', gap: '0.25rem', color: 'var(--accent)', borderColor: 'var(--accent)' }}
                onClick={handleRestoreOpportunity}
              >
                Restore from Archive
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-secondary"
                style={{ fontSize: '0.75rem', padding: '0.4rem 0.75rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}
                onClick={() => setShowArchiveModal(true)}
              >
                Archive
              </button>
            )}

            <button
              type="button"
              className="btn btn-secondary"
              style={{ fontSize: '0.75rem', padding: '0.4rem 0.75rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}
              onClick={() => setShowTaskHistoryModal(true)}
              title="View completed tasks history"
            >
              <Clock size={14} /> Task History ({isNew ? completedTempTasks.length : oppCompletedTasks.length})
            </button>
          </div>
        ) : (
          <div>
            {!isNew && (
              <button
                type="button"
                className="btn btn-secondary"
                style={{ fontSize: '0.75rem', padding: '0.4rem 0.75rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}
                onClick={() => setShowTaskHistoryModal(true)}
                title="View completed tasks history"
              >
                <Clock size={14} /> Task History ({isNew ? completedTempTasks.length : oppCompletedTasks.length})
              </button>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: '1rem' }}>
          {!isNew && (
            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={handleExportPDF}
              style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}
            >
              <FileText size={16} /> Export PDF
            </button>
          )}
          <button type="button" className="btn btn-secondary" onClick={() => onClose()}>
            {isReadOnly ? 'Close' : 'Cancel'}
          </button>
          {!isReadOnly && opportunity.status !== 'Archived' && (
            <button 
              type="submit" 
              className="btn btn-primary" 
              form="opportunityForm" 
              disabled={isSaving}
              style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}
            >
              {isSaving ? 'Saving...' : <><Save size={16} /> Save</>}
            </button>
          )}
        </div>
      </div>

      {showArchiveModal && (
        <ArchiveOpportunityModal 
          opportunity={opportunity} 
          onClose={() => setShowArchiveModal(false)} 
          onArchive={handleConfirmArchive} 
        />
      )}

      {showTaskHistoryModal && (
        <TaskHistoryModal
          opportunity={opportunity}
          completedTasks={isNew ? completedTempTasks : oppCompletedTasks}
          onClose={() => setShowTaskHistoryModal(false)}
        />
      )}
    </div>
  );
}
